// components/tier-branch/CreateEditBranchForm.tsx
// Create / Edit Branch form (inline panel, like the BranchPanel "Create
// Branch" screen). Includes the five lock toggles + enable switch.
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, TextField, Button, FormControlLabel, Switch,
  CircularProgress, Breadcrumbs, Link,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Branch, BranchInput } from "../../types/tierBranch";
import type { Notify } from "./useSnack";

const EMPTY: BranchInput = {
  name: "",
  whatsappNo: "",
  whatsappLink: "",
  creditLimit: 0,
  maxNewUsers: 0,
  maxDeposit: 0,
  registrationDelaySeconds: 0,
  registrationLocked: false,
  betLocked: false,
  depositLocked: false,
  withdrawLocked: false,
  batchedWithdrawal: false,
  notes: "",
  enabled: true,
};

const num = (v: string) => (v === "" ? 0 : Number(v));

const LOCKS: { key: keyof BranchInput; label: string }[] = [
  { key: "registrationLocked", label: "Registration Locked" },
  { key: "betLocked", label: "Bet Locked" },
  { key: "depositLocked", label: "Deposit Locked" },
  { key: "withdrawLocked", label: "Withdraw Locked" },
  { key: "batchedWithdrawal", label: "Batched Withdrawal" },
];

const CreateEditBranchForm: React.FC<{
  branch: Branch | null; // null = create mode
  onCancel: () => void;
  onSaved: () => void;
  notify: Notify;
}> = ({ branch, onCancel, onSaved, notify }) => {
  const [form, setForm] = useState<BranchInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branch) {
      const { id, newUsersToday, userCount, createdAt, updatedAt, ...rest } = branch;
      setForm(rest);
    } else {
      setForm(EMPTY);
    }
  }, [branch]);

  const set = <K extends keyof BranchInput>(key: K, value: BranchInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify("Branch name is required", "warning");
      return;
    }
    setSaving(true);
    try {
      if (branch) {
        await api.updateBranch(branch.id, form);
        notify("Branch updated", "success");
      } else {
        await api.createBranch(form);
        notify("Branch created", "success");
      }
      onSaved();
    } catch (e: any) {
      notify(e?.message || "Failed to save branch", "error");
    } finally {
      setSaving(false);
    }
  };

  const text = (label: string, key: keyof BranchInput, type: "text" | "number" = "text") => (
    <TextField
      label={label}
      type={type}
      size="small"
      fullWidth
      value={(form[key] as any) ?? (type === "number" ? 0 : "")}
      onChange={(e) => set(key, (type === "number" ? num(e.target.value) : e.target.value) as any)}
    />
  );

  return (
    <Card sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" onClick={onCancel}>
          Branch
        </Link>
        <Typography color="text.primary">{branch ? "Edit" : "Create"}</Typography>
      </Breadcrumbs>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {branch ? `Edit Branch — ${branch.name}` : "Create Branch"}
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
        {text("Branch Name", "name")}
        {text("WhatsApp No / Link", "whatsappNo")}
        {text("Credit Limit", "creditLimit", "number")}
        {text("Max New Users", "maxNewUsers", "number")}
        {text("Max Deposit", "maxDeposit", "number")}
        {text("Registration Delay (seconds)", "registrationDelaySeconds", "number")}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
          gap: 1,
          mt: 2,
        }}
      >
        {LOCKS.map((l) => (
          <FormControlLabel
            key={l.key}
            control={
              <Switch
                checked={Boolean(form[l.key])}
                onChange={(e) => set(l.key, e.target.checked as any)}
              />
            }
            label={l.label}
          />
        ))}
      </Box>

      <TextField
        label="Notes"
        size="small"
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        value={form.notes ?? ""}
        onChange={(e) => set("notes", e.target.value)}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <FormControlLabel
          control={
            <Switch checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          }
          label="Enabled"
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {branch ? "Save Changes" : "Create"}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default CreateEditBranchForm;
